import { lazy } from 'react'
import type { ComponentType } from 'react'

/**
 * Registry of rich Thinking pieces. Each owns its own interaction
 * pattern (registered in docs/thinking-pieces-tracker.md), lazy-loaded
 * so a piece using e.g. react-three-fiber doesn't add weight to pieces
 * that don't need it. Each piece renders in full on its own route
 * (/thinking/:slug, src/pages/ThinkingPiece.tsx) and collapses to a
 * stop in the index at the top of src/components/sections/Thinking.tsx
 * (src/components/thinking/kit/ThinkingIndex.tsx).
 */

export interface ThinkingPieceMeta {
  id: string
  title: string
  dek: string
  date: string
  /** Which spine this piece's index stop sits on: a belief vs. a build
      story, assigned by what the piece actually is, not registration order. */
  group: 'position' | 'system'
  /** 'note' pieces drop to the compact working-notes list in the index. */
  tier?: 'note'
}

export interface RegisteredPiece {
  meta: ThinkingPieceMeta
  Body: ComponentType
}

const NightField = lazy(() => import('./pieces/NightField'))
const AgentsOrgDesign = lazy(() => import('./pieces/AgentsOrgDesign'))
const TrustProduction = lazy(() => import('./pieces/TrustProduction'))
const AmbientAi = lazy(() => import('./pieces/AmbientAi'))
const GtdMini = lazy(() => import('./pieces/GtdMini'))
const AiDesignSlop = lazy(() => import('./pieces/AiDesignSlop'))
const LiveArtifacts = lazy(() => import('./pieces/LiveArtifacts'))
const PersonalKnowledgeGraph = lazy(() => import('./pieces/PersonalKnowledgeGraph'))
const HomeAutomation = lazy(() => import('./pieces/HomeAutomation'))
const CompoundEngineering = lazy(() => import('./pieces/CompoundEngineering'))
const AutonomousAgents = lazy(() => import('./pieces/AutonomousAgents'))
const LlmInference = lazy(() => import('./pieces/LlmInference'))
const EdgeTypescript = lazy(() => import('./pieces/EdgeTypescript'))
const HopFlights = lazy(() => import('./pieces/HopFlights'))
const KrootsMap = lazy(() => import('./pieces/KrootsMap'))
const TollFreeMaze = lazy(() => import('./pieces/TollFreeMaze'))
const AgentDesks = lazy(() => import('./pieces/AgentDesks'))
const WorkingNotes2026 = lazy(() => import('./pieces/WorkingNotes2026'))
const PdfCharspaceGotcha = lazy(() => import('./pieces/PdfCharspaceGotcha'))
const ThisPage = lazy(() => import('./pieces/ThisPage'))
const FieldGuide = lazy(() => import('./pieces/FieldGuide/FieldGuide'))

export const registeredPieces: RegisteredPiece[] = [
  {
    meta: {
      id: 'night-field',
      title: 'What the night field broke',
      dek: 'The homepage is a real 3D scene built by an agent team. Here is everything that went black, spun around the wrong point, or skated across the ground before it worked.',
      date: '2026-07-22',
      group: 'system',
    },
    Body: NightField,
  },
  {
    meta: {
      id: 'agents-org-design',
      title: 'Agents are an org design problem',
      dek: 'A standing team of coding agents, each with a named desk and a real schedule. One boundary has never moved, no matter how good the model got.',
      date: '2026-07-22',
      group: 'position',
    },
    Body: AgentsOrgDesign,
  },
  {
    meta: {
      id: 'trust-production',
      title: 'Trust is a runtime property',
      dek: 'A policy that says harmful content never surfaces without review is enforced by a latency number, not by the document that states it. Drag the slider.',
      date: '2026-07-22',
      group: 'position',
    },
    Body: TrustProduction,
  },
  {
    meta: {
      id: 'ambient-ai',
      title: 'AI should be present without being summoned',
      dek: 'The chat box makes you stop and decide what to ask. An ambient layer reads the room instead.',
      date: '2026-07-22',
      group: 'position',
    },
    Body: AmbientAi,
  },
  {
    meta: {
      id: 'gtd-mini',
      title: 'The machine that runs my todo list while I sleep',
      dek: 'A headless Mac Mini, a cron job every fifteen minutes, and a timeline of what it actually did.',
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: GtdMini,
  },
  {
    meta: {
      id: 'ai-design-slop',
      title: 'Why every AI-generated UI looks the same',
      dek: 'Purple-to-blue gradient cards, a centered icon grid, low-contrast dark mode. The tell is hardcoded hex drifting away from a token file nobody is checking against.',
      date: '2026-07-22',
      group: 'position',
    },
    Body: AiDesignSlop,
  },
  {
    meta: {
      id: 'live-artifacts',
      title: 'Live artifacts are three different architectures',
      dek: 'A sandboxed page with a versioned link, a full dev environment in a browser tab, and real production component code all get called the same thing, and each trades control against containment differently.',
      date: '2026-07-22',
      group: 'position',
      tier: 'note',
    },
    Body: LiveArtifacts,
  },
  {
    meta: {
      id: 'personal-knowledge-graph',
      title: 'A knowledge base becomes a graph once something else can query it',
      dek: 'PARA and Zettelkasten pushed back on hierarchy for a human reader. The graph shape only starts to matter once an agent can traverse it multiple hops and audit what it finds.',
      date: '2026-07-22',
      group: 'position',
      tier: 'note',
    },
    Body: PersonalKnowledgeGraph,
  },
  {
    meta: {
      id: 'home-automation',
      title: 'Automation is arbitration plumbing',
      dek: 'A sensor reports, a hub decides, an actuator acts. From inside the house, each of those three roles fails silently in its own way.',
      date: '2026-07-22',
      group: 'position',
      tier: 'note',
    },
    Body: HomeAutomation,
  },
  {
    meta: {
      id: 'compound-engineering',
      title: 'Compound engineering applied to a personal site',
      dek: "Five real pull requests from this site's own git history (a build, a review, a three-line fix, an extraction, and a compliance rule) show what compounding actually looks like when a fix from one PR gets applied automatically three PRs later without anyone repeating the instruction.",
      date: '2026-07-22',
      group: 'system',
    },
    Body: CompoundEngineering,
  },
  {
    meta: {
      id: 'autonomous-agents',
      title: 'What "autonomous" actually means in production',
      dek: 'Click through what an agent can take back and what it can’t: the two lists have almost nothing in common, and that gap is the entire job of designing one.',
      date: '2026-07-22',
      group: 'position',
    },
    Body: AutonomousAgents,
  },
  {
    meta: {
      id: 'llm-inference',
      title: 'Memory is the toll booth',
      dek: "Prefill runs once, in parallel. Every token after that is its own round trip to memory to reload the model's weights and KV cache, which is why decode speed lives or dies on memory bandwidth.",
      date: '2026-07-22',
      group: 'position',
      tier: 'note',
    },
    Body: LlmInference,
  },
  {
    meta: {
      id: 'edge-typescript',
      title: 'TypeScript at the edge',
      dek: 'Cloudflare Workers run TypeScript inside V8 isolates instead of VMs or containers: sub-millisecond starts, no cold-start boot cost, and a stack built around that same disposable unit of compute.',
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: EdgeTypescript,
  },
  {
    meta: {
      id: 'hop-flights',
      title: 'A computed verdict can still be wrong',
      dek: "hop.flights' whole pitch is a verdict that's computed, not guessed. For any search with more than one passenger, it was computing that verdict from two numbers that didn't describe the same trip.",
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: HopFlights,
  },
  {
    meta: {
      id: 'kroots-map',
      title: 'Kroots: mapping a graph nobody else could see',
      dek: "A one-person kids'-media venture with no team still needed an org chart, so a single map-of-content page (four clusters of working documents around one hub) had to do the job instead. Trace the clusters back to the hub.",
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: KrootsMap,
  },
  {
    meta: {
      id: 'twilio-compliance',
      title: 'The toll-free verification maze',
      dek: 'Twilio rejected the same toll-free application twice, for two different fields, before it explained why. The map of that maze, built one collision at a time.',
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: TollFreeMaze,
  },
  {
    meta: {
      id: 'agent-desks',
      title: 'A standing team with named desks',
      dek: "A desk on an org chart isn't real until it has a trigger, a boundary, and a timestamp. Click through four generic desks and see what each one actually shipped last.",
      date: '2026-07-22',
      group: 'position',
    },
    Body: AgentDesks,
  },
  {
    meta: {
      id: '2026-goals',
      title: 'Working notes: 2026',
      dek: 'Four generic goal categories, one review cadence each, except the one with no calendar entry at all.',
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: WorkingNotes2026,
  },
  {
    meta: {
      id: 'pdf-charspace',
      title: 'The PDF gotcha that cost an afternoon',
      dek: "A hairline rule in a table looked wrong by 10 to 25 points, only on rows after a letterspaced date chip. The bug wasn't in the alignment math; it was in a PDF operator that survives past where you'd expect it to end. Drag the slider to leak it yourself.",
      date: '2026-07-22',
      group: 'system',
      tier: 'note',
    },
    Body: PdfCharspaceGotcha,
  },
  {
    meta: {
      id: 'this-page',
      title: 'How this backlog got built',
      dek: "Twenty Thinking pieces, built by parallel agents in isolated worktrees and merged through one shared registry file, including the batch that got built twice and the draft that almost named a competitor's model by brand.",
      date: '2026-07-22',
      group: 'system',
    },
    Body: ThisPage,
  },
  {
    meta: {
      id: 'field-guide',
      title: 'Tell it as a place',
      dek: 'A field guide to a site that tells your story instead of listing it: find the spine, build the world in Blender and FLORA, then hand the playhead to the reader’s scroll. Every technique it teaches, it performs.',
      date: '2026-07-29',
      group: 'system',
    },
    Body: FieldGuide,
  },
]
